/**
 * Shared SendGrid dialogs used by both the master page and each client's
 * SendGrid tab:
 *   • CreateSubuserDialog — spin up a brand-new SendGrid subuser for a
 *     client, auto-generate a strong password, auto-pick an IP (with an
 *     optional advanced picker), and optionally attach the subuser to a
 *     client in one shot.
 *   • DomainAuthWizard — walks the admin through creating a whitelabel
 *     domain (SPF+DKIM) + link branding (click/open tracking) in
 *     SendGrid, displays the CNAME records the client's DNS needs to
 *     publish, provides "Verify DNS" buttons, and includes short SPF /
 *     DMARC guidance blocks.
 *
 * Both dialogs are dumb — they take the necessary props (defaultClientId,
 * defaultDomain, etc.) and render the same shape regardless of caller.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, UserPlus, Copy, ShieldCheck, ShieldAlert, RefreshCw,
  Info, Wand2, ExternalLink, CheckCircle2, XCircle, ChevronRight,
} from "lucide-react";

const errText = (e, f = "Failed") =>
  e?.response?.data?.detail || e?.message || f;

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label || "Copied"}`);
  } catch {
    toast.error("Copy failed — select and copy manually");
  }
};

// ─────────────────────────────────────────────────────────────
// Create Subuser dialog
// ─────────────────────────────────────────────────────────────
export function CreateSubuserDialog({
  open, onOpenChange, clients = [], defaultClientId = "",
  onCreated,
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState(defaultClientId || "");
  const [ips, setIps] = useState([]);            // master account IPs
  const [pickedIps, setPickedIps] = useState([]); // selected subset (&apos;&apos; = auto)
  const [advanced, setAdvanced] = useState(false);
  const [ipsLoading, setIpsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);   // {username, password, ...}

  // Reset state whenever the dialog re-opens
  useEffect(() => {
    if (!open) return;
    setUsername(""); setEmail("");
    setClientId(defaultClientId || "");
    setPickedIps([]); setAdvanced(false); setResult(null);
    setIpsLoading(true);
    api.get("/sendgrid/master/ips")
      .then(({ data }) => setIps(data.ips || []))
      .catch((e) => toast.error(errText(e, "Failed to load IPs")))
      .finally(() => setIpsLoading(false));
  }, [open, defaultClientId]);

  // When the admin picks a client, prefill the username + email from the
  // company name — nicest starting point they can then edit.
  useEffect(() => {
    if (!clientId) return;
    const c = clients.find((x) => x.client_id === clientId);
    if (!c) return;
    const slug = (c.company_name || c.name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 24);
    if (slug && !username) setUsername(`ib-${slug}`);
   
  }, [clientId]);

  const toggleIp = (ip) => {
    setPickedIps((prev) => prev.includes(ip)
      ? prev.filter((x) => x !== ip)
      : [...prev, ip]);
  };

  const submit = async () => {
    if (!username.trim()) { toast.error("Username is required"); return; }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      toast.error("Email must be a valid address"); return;
    }
    setSaving(true);
    try {
      const body = {
        username: username.trim(),
        email: email.trim(),
        client_id: clientId || null,
      };
      if (advanced && pickedIps.length > 0) body.ips = pickedIps;
      const { data } = await api.post("/sendgrid/master/subusers", body);
      setResult(data);
      toast.success(`Subuser ${data.username} created`);
      if (onCreated) onCreated(data);
    } catch (e) {
      toast.error(errText(e, "Create failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm border-zinc-200"
        data-testid="sg-create-subuser-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <UserPlus className="h-4 w-4 text-[#002FA7]" />
            Create new SendGrid subuser
          </DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-600 pt-1">
            Provisions a brand-new subuser under the master SendGrid
            account. Optionally link it to an Iceberg client so its 30-day
            stats and API keys are scoped to that client automatically.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-sm bg-emerald-50 border border-emerald-200 p-3">
              <div className="flex items-center gap-2 font-display font-bold text-emerald-900 text-sm">
                <CheckCircle2 className="h-4 w-4" /> Subuser created
              </div>
              <div className="text-[11px] text-emerald-800 mt-1">
                {result.assigned_client_name
                  ? <>Linked to <b>{result.assigned_client_name}</b>.</>
                  : "Not linked to a client yet."}
              </div>
            </div>
            <FieldRow label="Username" value={result.username} />
            <FieldRow label="Email" value={result.email} />
            <FieldRow label="Assigned IPs" value={(result.ips || []).join(", ")} />
            {result.generated_password && (
              <>
                <FieldRow label="Password (shown ONCE)" value={result.generated_password} mono
                  hint="SendGrid cannot show this again. Copy + store now (e.g. in 1Password)." />
                <div className="rounded-sm bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800 flex gap-2">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  This is the login password for app.sendgrid.com. If the
                  client needs SMTP or REST API access from their own
                  system, generate an API key from the subuser row instead.
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {clients.length > 0 && (
              <div>
                <Label className="label-overline">Link to Iceberg client</Label>
                <select value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="mt-1 w-full rounded-sm border border-zinc-300 px-2 py-2 text-sm bg-white"
                  data-testid="sg-create-client">
                  <option value="">— No client (create standalone) —</option>
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.company_name || c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Subuser username</Label>
                <Input value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="ib-royalnewcastleaeroclub"
                  className="mt-1 rounded-sm font-mono text-sm"
                  data-testid="sg-create-username" />
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Lowercase, no spaces. Prefix with <code>ib-</code> so it&apos;s obvious it&apos;s ours.
                </p>
              </div>
              <div>
                <Label className="label-overline">Contact email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="hello@rnac.com.au"
                  className="mt-1 rounded-sm text-sm"
                  data-testid="sg-create-email" />
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  SendGrid sends account emails here.
                </p>
              </div>
            </div>

            {/* IP picker */}
            <div className="rounded-sm border border-zinc-200 p-3">
              <div className="flex items-center justify-between">
                <div className="label-overline">Sending IP</div>
                <button type="button" onClick={() => setAdvanced((v) => !v)}
                  className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7] hover:underline"
                  data-testid="sg-create-toggle-advanced">
                  {advanced ? "Auto-pick" : "Advanced · pick IP"}
                </button>
              </div>
              {ipsLoading ? (
                <div className="py-2 text-[11px] text-zinc-500">
                  <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                  Loading master IPs…
                </div>
              ) : ips.length === 0 ? (
                <div className="text-[11px] text-red-700 mt-1">
                  Your master account has no assigned IPs — SendGrid support needs to attach one.
                </div>
              ) : !advanced ? (
                <div className="text-[11px] text-zinc-600 mt-1">
                  Will auto-pick <code className="font-mono">{ips[0].ip}</code>
                  {" · "}pool: <code>{(ips[0].pools || []).join(", ") || "(none)"}</code>
                </div>
              ) : (
                <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                  {ips.map((row) => {
                    const picked = pickedIps.includes(row.ip);
                    return (
                      <label key={row.ip}
                        className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 text-[11px] cursor-pointer ${
                          picked ? "bg-blue-50 border-blue-200" : "border-zinc-200 hover:bg-zinc-50"}`}>
                        <input type="checkbox" checked={picked}
                          onChange={() => toggleIp(row.ip)} />
                        <code className="font-mono">{row.ip}</code>
                        <span className="text-zinc-500 truncate">
                          pool: {(row.pools || []).join(", ") || "(none)"}
                          {" · "}subusers: {(row.assigned_subusers || []).length}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          {result ? (
            <Button onClick={() => onOpenChange(false)} className="rounded-sm"
              data-testid="sg-create-close">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}
                className="rounded-sm">Cancel</Button>
              <Button onClick={submit} disabled={saving || ipsLoading}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                data-testid="sg-create-submit">
                {saving
                  ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</>
                  : <><UserPlus className="h-4 w-4 mr-1.5" /> Create subuser</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, value, mono, hint }) {
  return (
    <div className="rounded-sm border border-zinc-200 p-2 bg-white">
      <div className="flex items-center justify-between gap-2">
        <div className="label-overline">{label}</div>
        <button onClick={() => copyText(value, `${label} copied`)}
          className="text-[10px] uppercase tracking-wider text-[#002FA7] hover:underline">
          <Copy className="h-3 w-3 inline mr-0.5" /> Copy
        </button>
      </div>
      <div className={`${mono ? "font-mono" : ""} text-sm mt-0.5 break-all`}>
        {value || "—"}
      </div>
      {hint && (
        <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Domain authentication wizard
// ─────────────────────────────────────────────────────────────
export function DomainAuthWizard({
  open, onOpenChange, defaultDomain = "", subuser = "", clientId = "",
  onComplete,
}) {
  const [step, setStep] = useState(1);          // 1..4
  const [rootDomain, setRootDomain] = useState(defaultDomain);
  const [subdomain, setSubdomain] = useState("em"); // SendGrid recommends "em"
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [domain, setDomain] = useState(null);   // whitelabel domain object

  const [linkSub, setLinkSub] = useState("url"); // SendGrid link-brand default
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkBrand, setLinkBrand] = useState(null);

  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [verifyingLink, setVerifyingLink] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setRootDomain(defaultDomain); setSubdomain("em");
    setDomain(null); setLinkBrand(null); setLinkSub("url");
  }, [open, defaultDomain]);

  // DNS records the client&apos;s DNS admin needs to publish. SendGrid returns
  // these as an object of `{host, type, data, valid?}` items nested under
  // `dns.mail_cname`, `dns.dkim1`, `dns.dkim2` for whitelabel domains and
  // similar for link branding.
  const dnsRows = useMemo(() => {
    if (!domain?.dns) return [];
    return Object.entries(domain.dns).map(([slot, r]) => ({
      slot, host: r.host, type: (r.type || "cname").toUpperCase(),
      data: r.data, valid: r.valid,
    }));
  }, [domain]);
  const linkRows = useMemo(() => {
    if (!linkBrand?.dns) return [];
    return Object.entries(linkBrand.dns).map(([slot, r]) => ({
      slot, host: r.host, type: (r.type || "cname").toUpperCase(),
      data: r.data, valid: r.valid,
    }));
  }, [linkBrand]);

  const createDomain = async () => {
    if (!rootDomain.trim()) { toast.error("Enter a root domain"); return; }
    setCreatingDomain(true);
    try {
      const { data } = await api.post("/sendgrid/whitelabel/domains", {
        domain: rootDomain.trim(),
        subdomain: subdomain.trim() || null,
        subuser: subuser || null,
        client_id: clientId || null,
        automatic_security: true,
      });
      setDomain(data); setStep(2);
    } catch (e) { toast.error(errText(e, "Create failed")); }
    finally { setCreatingDomain(false); }
  };

  const verifyDomain = async () => {
    if (!domain?.id) return;
    setVerifyingDomain(true);
    try {
      const { data } = await api.post(
        `/sendgrid/whitelabel/domains/${domain.id}/validate`);
      // Merge validation flags into the domain state.
      const merged = { ...domain, valid: !!data.valid };
      if (data.validation_results && domain.dns) {
        const dns = { ...domain.dns };
        Object.entries(data.validation_results).forEach(([slot, r]) => {
          if (dns[slot]) dns[slot] = { ...dns[slot], valid: !!r.valid, reason: r.reason };
        });
        merged.dns = dns;
      }
      setDomain(merged);
      if (data.valid) toast.success("Domain verified");
      else toast.error("DNS not verified yet — records may still be propagating");
    } catch (e) { toast.error(errText(e, "Verify failed")); }
    finally { setVerifyingDomain(false); }
  };

  const createLink = async () => {
    if (!rootDomain.trim()) return;
    setCreatingLink(true);
    try {
      const { data } = await api.post("/sendgrid/whitelabel/links", {
        domain: rootDomain.trim(),
        subdomain: linkSub.trim() || null,
        subuser: subuser || null,
        client_id: clientId || null,
      });
      setLinkBrand(data); setStep(3);
    } catch (e) { toast.error(errText(e, "Create link brand failed")); }
    finally { setCreatingLink(false); }
  };

  const verifyLink = async () => {
    if (!linkBrand?.id) return;
    setVerifyingLink(true);
    try {
      const { data } = await api.post(
        `/sendgrid/whitelabel/links/${linkBrand.id}/validate`);
      const merged = { ...linkBrand, valid: !!data.valid };
      if (data.validation_results && linkBrand.dns) {
        const dns = { ...linkBrand.dns };
        Object.entries(data.validation_results).forEach(([slot, r]) => {
          if (dns[slot]) dns[slot] = { ...dns[slot], valid: !!r.valid, reason: r.reason };
        });
        merged.dns = dns;
      }
      setLinkBrand(merged);
      if (data.valid) toast.success("Link branding verified");
      else toast.error("DNS not verified yet");
    } catch (e) { toast.error(errText(e, "Verify failed")); }
    finally { setVerifyingLink(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-sm border-zinc-200 max-h-[90vh] overflow-y-auto"
        data-testid="sg-domain-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Wand2 className="h-4 w-4 text-[#002FA7]" />
            Domain authentication wizard
            {subuser && (
              <Badge variant="outline" className="rounded-sm text-[10px] font-mono ml-1">
                subuser {subuser}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-600 pt-1">
            Walks you through the four steps SendGrid requires to send
            branded, spam-safe email from a client&apos;s domain: (1) create a
            whitelabel SPF+DKIM domain, (2) hand the CNAMEs to whoever
            owns the DNS zone, (3) add click/open-tracking branding,
            (4) publish a DMARC record.
          </DialogDescription>
        </DialogHeader>

        <StepStrip step={step} />

        {/* STEP 1 — root domain */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="rounded-sm border border-zinc-200 p-3">
              <div className="label-overline">Root domain to authenticate</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
                <div>
                  <Label className="text-[11px]">Root domain</Label>
                  <Input value={rootDomain} onChange={(e) => setRootDomain(e.target.value)}
                    placeholder="rnac.com.au"
                    className="mt-1 font-mono text-sm rounded-sm"
                    data-testid="sg-wizard-domain" />
                </div>
                <div>
                  <Label className="text-[11px]">Send subdomain</Label>
                  <Input value={subdomain} onChange={(e) => setSubdomain(e.target.value)}
                    placeholder="em"
                    className="mt-1 font-mono text-sm rounded-sm"
                    data-testid="sg-wizard-subdomain" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                SendGrid will create CNAMEs like <code>em.{rootDomain || "yourdomain.com"}</code>{" "}
                that route mail to their servers. <code>em</code> is SendGrid&apos;s
                convention — keep it unless the DNS already has one.
              </p>
            </div>
            <Button onClick={createDomain} disabled={creatingDomain || !rootDomain}
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
              data-testid="sg-wizard-create-domain">
              {creatingDomain
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</>
                : <>Create whitelabel domain <ChevronRight className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
        )}

        {/* STEP 2 — SPF/DKIM DNS records */}
        {step >= 2 && domain && (
          <div className="space-y-3 mt-3">
            <DnsBlock title={`SPF + DKIM · ${domain.domain}`}
              rows={dnsRows}
              valid={domain.valid}
              onVerify={verifyDomain} verifying={verifyingDomain}
              blurb={`Ask whoever manages ${domain.domain}'s DNS to publish
                     these ${dnsRows.length} CNAME records. Google Workspace,
                     GoDaddy, Cloudflare, whatever — they all support CNAMEs.
                     Click Verify once they've told you it's done — DNS
                     propagation is usually <15 minutes, occasionally an hour.`} />
            {step === 2 && (
              <Button onClick={createLink} disabled={creatingLink}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                data-testid="sg-wizard-create-link">
                {creatingLink
                  ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</>
                  : <>Add click / open tracking branding <ChevronRight className="h-4 w-4 ml-1" /></>}
              </Button>
            )}
          </div>
        )}

        {/* STEP 3 — link branding */}
        {step >= 3 && linkBrand && (
          <div className="space-y-3 mt-3">
            <DnsBlock title={`Link branding · ${linkBrand.domain}`}
              rows={linkRows}
              valid={linkBrand.valid}
              onVerify={verifyLink} verifying={verifyingLink}
              blurb="These CNAMEs rewrite the click-tracking URLs inside
                    outgoing emails so links look like url.rnac.com.au
                    instead of sendgrid.net — big deal for deliverability
                    and open rates." />
            {step === 3 && (
              <Button onClick={() => setStep(4)} className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A]">
                Next · DMARC guidance <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}

        {/* STEP 4 — DMARC guidance */}
        {step === 4 && (
          <div className="rounded-sm border border-zinc-200 p-3 mt-3 space-y-2">
            <div className="label-overline">DMARC record (recommended)</div>
            <p className="text-[12px] text-zinc-700 leading-relaxed">
              SPF + DKIM (the CNAMEs above) tell mail servers who&apos;s allowed
              to send. DMARC tells them what to do when a message fails
              those checks — and it&apos;s the single record with the biggest
              impact on inbox placement.
            </p>
            <div className="rounded-sm bg-zinc-900 text-emerald-200 p-3">
              <div className="text-[9px] font-mono uppercase text-emerald-400 tracking-wider mb-1">
                TXT record on _dmarc.{rootDomain || "yourdomain.com"}
              </div>
              <div className="font-mono text-[11px] break-all leading-tight">
                v=DMARC1; p=none; rua=mailto:dmarc@{rootDomain || "yourdomain.com"}; ruf=mailto:dmarc@{rootDomain || "yourdomain.com"}; fo=1; adkim=r; aspf=r; pct=100
              </div>
              <button className="mt-2 text-[10px] font-mono uppercase text-emerald-300 hover:underline"
                onClick={() => copyText(
                  `v=DMARC1; p=none; rua=mailto:dmarc@${rootDomain}; ruf=mailto:dmarc@${rootDomain}; fo=1; adkim=r; aspf=r; pct=100`,
                  "DMARC record copied")}>
                <Copy className="h-3 w-3 inline mr-0.5" /> Copy
              </button>
            </div>
            <ul className="text-[11px] text-zinc-600 space-y-1 list-disc pl-4 mt-1">
              <li><b>p=none</b> — monitor only (start here for 2 weeks, review the aggregate reports at <code>dmarc@…</code>).</li>
              <li>After 2 weeks with no legitimate mail failing, tighten to <b>p=quarantine</b>.</li>
              <li>After another 2-4 weeks, tighten to <b>p=reject</b> for full spoofing protection.</li>
              <li>Set up a shared inbox at <code>dmarc@{rootDomain}</code> — or use a free reporting service like DMARCLY / dmarcian to make sense of the XML reports.</li>
            </ul>
            <div className="pt-2 flex gap-2 flex-wrap">
              <a href="https://dmarc.org/overview/" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline">
                DMARC deep-dive <ExternalLink className="h-3 w-3" />
              </a>
              <a href={`https://mxtoolbox.com/SuperTool.aspx?action=dmarc%3a${rootDomain}&run=toolpage`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline">
                Check {rootDomain}&apos;s DMARC live <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="rounded-sm">Close</Button>
          {step === 4 && (
            <Button onClick={() => { onComplete && onComplete({ domain, linkBrand }); onOpenChange(false); }}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
              data-testid="sg-wizard-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepStrip({ step }) {
  const steps = [
    { n: 1, label: "Domain" },
    { n: 2, label: "SPF + DKIM DNS" },
    { n: 3, label: "Link branding" },
    { n: 4, label: "DMARC" },
  ];
  return (
    <div className="flex gap-2 flex-wrap mt-2 mb-1">
      {steps.map((s) => {
        const active = step === s.n;
        const done = step > s.n;
        return (
          <div key={s.n}
            className={`flex items-center gap-1.5 rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${
              done ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : active ? "bg-blue-50 text-[#002FA7] border border-blue-200"
                : "bg-zinc-50 text-zinc-500 border border-zinc-200"}`}>
            <span className={`inline-block h-4 w-4 rounded-full text-white font-bold text-[9px] leading-4 text-center ${
              done ? "bg-emerald-600" : active ? "bg-[#002FA7]" : "bg-zinc-400"}`}>
              {s.n}
            </span>
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

function DnsBlock({ title, rows, valid, onVerify, verifying, blurb }) {
  return (
    <div className="rounded-sm border border-zinc-200 p-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="label-overline">{title}</div>
          <p className="text-[11px] text-zinc-600 mt-1 max-w-2xl">{blurb}</p>
        </div>
        <div className="flex items-center gap-2">
          {valid == null ? (
            <Badge variant="outline" className="rounded-sm text-[10px]">
              <ShieldAlert className="h-3 w-3 mr-1" /> Pending DNS
            </Badge>
          ) : valid ? (
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
              <ShieldCheck className="h-3 w-3 mr-1" /> Verified
            </Badge>
          ) : (
            <Badge className="rounded-sm bg-red-100 text-red-800 border-red-200 text-[10px]">
              <XCircle className="h-3 w-3 mr-1" /> Not verified
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={onVerify} disabled={verifying}
            className="rounded-sm h-7"
            data-testid={`sg-verify-${title.split(" ")[0].toLowerCase()}`}>
            {verifying ? <Loader2 className="h-3 w-3 animate-spin" />
              : <><RefreshCw className="h-3 w-3 mr-1" /> Verify DNS</>}
          </Button>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <div key={r.slot}
            className="rounded-sm border border-zinc-100 bg-zinc-50 p-2">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">
                  {r.slot} · {r.type}
                </div>
                <div className="font-mono text-[11px] mt-0.5">
                  <span className="text-zinc-500">host: </span>
                  <span className="font-bold break-all">{r.host}</span>
                </div>
                <div className="font-mono text-[11px]">
                  <span className="text-zinc-500">value: </span>
                  <span className="break-all">{r.data}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {r.valid != null && (r.valid
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  : <XCircle className="h-4 w-4 text-red-600" />)}
                <button
                  onClick={() => copyText(r.host, `${r.slot} host copied`)}
                  className="text-[9px] uppercase tracking-wider text-[#002FA7] hover:underline whitespace-nowrap">
                  <Copy className="h-3 w-3 inline" /> host
                </button>
                <button
                  onClick={() => copyText(r.data, `${r.slot} value copied`)}
                  className="text-[9px] uppercase tracking-wider text-[#002FA7] hover:underline whitespace-nowrap">
                  <Copy className="h-3 w-3 inline" /> value
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
